import { c as _c } from "react/compiler-runtime";
import { useQuery } from "@tanstack/react-query";
import React, { useCallback, useEffect, useState } from "react";
import { Controller, FormProvider, useFormContext } from "react-hook-form";
import LocationField from "@/components/patient/create-patient-form/location-field";
import { MARKETING_OPTIONS, fromSelectValue, toSelectValue } from "@/components/patient/create-patient-form/marketing-field";
import { LeadSourceSelect } from "@/components/patient/lead-source-select";
import { Button, DateOfBirthInput, Dialog, DialogBody, DialogContent, DialogTrigger, Input, Label } from "@/components/ui";
import AddressInput from "@/components/ui/address-input";
import { DialogClose, DialogFooter, DialogHeader, DialogTitle } from "@/components/ui/dialog";
import PhoneNumberInput from "@/components/ui/phone-number-input";
import { SearchSelect, SearchSelectContent, SearchSelectItem, SearchSelectTrigger, SearchSelectValue, Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { useTenant } from "@/contexts/tenant-provider";
import { useToast } from "@/contexts/toaster";
import { usePatientForm } from "@/hooks/use-patient-form";
import { formatToHumanReadable } from "@/lib/format-data";
import { parseError } from "@/lib/parse-error";
import { isPatientMrnEnabledTenant } from "@/lib/patient-mrn";
import { Gender } from "@/types/enums/gender";
import { PatientUpdate } from "@/types/patient";
import { useUpdatePatientMutation } from "@/api/provider/user/patient/patient-mutation";
import { patientQueryOptions } from "@/api/provider/user/patient/patient-query";
interface DemographicEditDialogProps {
  children?: React.ReactNode;
  patientId: string;
  /**
   * Optional controlled open state. When provided (with `onOpenChange`) the
   * dialog is externally driven — used to open it imperatively from a toast
   * action, which can't host a `DialogTrigger`. Omit both to keep the default
   * uncontrolled trigger-child behavior (e.g. the Demographics section's Edit
   * button).
   */
  open?: boolean;
  onOpenChange?: (open: boolean) => void;
}

/** The MRN rides along in the form for display; it is read-only and never saved from here. */
type DemographicFormValues = PatientUpdate & {
  mrn?: string | null;
};
const DemographicEditDialog: React.FC<DemographicEditDialogProps> = ({
  children,
  patientId,
  open: controlledOpen,
  onOpenChange
}) => {
  const [isLoading, setIsLoading] = useState<{
    patient: boolean;
  }>({
    patient: false
  });
  const {
    data: patient
  } = useQuery({
    ...patientQueryOptions.detail(patientId),
    enabled: !!patientId
  });
  const updatePatientMutation = useUpdatePatientMutation(patientId);
  const {
    toast
  } = useToast();
  const [internalOpen, setInternalOpen] = useState(false);
  // Controlled when a parent passes `open`; otherwise fall back to local state
  // so existing trigger-child usages are unaffected.
  const isControlled = controlledOpen !== undefined;
  const open = isControlled ? controlledOpen : internalOpen;
  const setOpen = useCallback((next: boolean) => {
    if (!isControlled) setInternalOpen(next);
    onOpenChange?.(next);
  }, [isControlled, onOpenChange]);
  const initialData = patient ? {
    firstName: patient.firstName ?? undefined,
    middleName: patient.middleName ?? undefined,
    lastName: patient.lastName ?? undefined,
    email: patient.email ?? undefined,
    dateOfBirth: patient.dateOfBirth ?? undefined,
    phoneNumber: patient.phoneNumber ?? undefined,
    gender: patient.gender ?? undefined,
    pronouns: patient.pronouns ?? undefined,
    preferredName: patient.preferredName ?? undefined,
    address: patient.address ?? undefined,
    city: patient.city ?? undefined,
    state: patient.state ?? undefined,
    zipCode: patient.zipCode ?? undefined,
    addressLineTwo: patient.addressLineTwo ?? undefined,
    externalId: patient.externalId ?? undefined,
    mrn: patient.mrn ?? undefined,
    primaryLocationId: patient.primaryLocationId ?? undefined,
    leadSource: patient.leadSource ?? undefined,
    optedInToSmsMarketing: patient.optedInToSmsMarketing ?? undefined,
    optedInToEmailMarketing: patient.optedInToEmailMarketing ?? undefined,
    optedInToSocialMedia: patient.optedInToSocialMedia ?? undefined,
    aiScribeConsent: patient.aiScribeConsent ?? undefined
  } : undefined;
  const methods = usePatientForm<DemographicFormValues>("edit", initialData, patient || undefined);
  const {
    handleSubmit,
    reset,
    getValues
  } = methods;
  useEffect(() => {
    if (!patient || !initialData) {
      return;
    }

    // Don't reset the form while a save is in-flight — the optimistic update
    // changes `patient`, which would overwrite the user's pending edits.
    if (isLoading.patient) {
      return;
    }
    reset(initialData);
  }, [patient, reset, isLoading.patient]);
  const showPatientNotFoundToast = useCallback(() => {
    toast({
      title: "Patient Not Found",
      description: `Patient with ID ${patient?.id} not found.`,
      variant: "error"
    });
  }, [patient?.id, toast]);
  const handleFormSubmit = useCallback(async (data: DemographicFormValues) => {
    setIsLoading(prev => ({
      ...prev,
      patient: true
    }));
    if (!patient) {
      showPatientNotFoundToast();
      setIsLoading(prev_0 => ({
        ...prev_0,
        patient: false
      }));
      return false;
    }
    try {
      // Get all form values to ensure all registered fields are included
      const allFormValues = getValues();
      // Merge initial data, all form values, and submitted data
      // This ensures fields like pronouns are included even if they weren't changed
      // The MRN is shown for reference only; it is never sent back.
      const {
        mrn: _mrn,
        ...mergedData
      } = {
        ...initialData,
        ...allFormValues,
        ...data
      };
      const payload = Object.fromEntries(Object.entries(mergedData).filter(([, value]) => value !== undefined).map(([key, value_0]) => [key, value_0 === "" ? null : value_0])) as PatientUpdate;
      const response = await updatePatientMutation.mutateAsync(payload);
      if (response) {
        reset({
          ...response
        });
        setOpen(false);
      } else {
        toast({
          title: "Update Failed",
          description: "Failed to update patient demographics.",
          variant: "error"
        });
      }
    } catch (error) {
      const message = await parseError(error);
      toast({
        title: "Update Failed",
        description: message,
        variant: "error"
      });
    } finally {
      setIsLoading(prev => ({
        ...prev,
        patient: false
      }));
    }
  }, [patient, initialData, getValues, toast, setIsLoading, reset, setOpen, showPatientNotFoundToast, updatePatientMutation]);
  return <Dialog open={open} onOpenChange={setOpen}>
      {children && <DialogTrigger asChild onClick={() => setOpen(true)}>
          {children}
        </DialogTrigger>}
      <DialogContent>
        <DialogHeader>
          <DialogTitle>Edit Demographics</DialogTitle>
        </DialogHeader>

        <DialogBody>
          <FormProvider {...methods}>
            <form id="demographic-edit-form" onSubmit={handleSubmit(async data_0 => await handleFormSubmit(data_0))} className="space-y-6" noValidate>
              <PersonalInformationSection />
              <AddressInput className="-mt-2" />
              <MarketingPreferencesSection />
              <LocationField className="mb-4" />
            </form>
          </FormProvider>
        </DialogBody>

        <DialogFooter>
          <DialogClose asChild>
            <Button type="button" variant="secondary" onClick={() => setOpen(false)}>
              Cancel
            </Button>
          </DialogClose>
          <Button type="submit" form="demographic-edit-form" isLoading={isLoading.patient}>
            Save Changes
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>;
};

// Exported for unit testing the External ID read-only behavior in isolation.
export const PersonalInformationSection = () => {
  const $ = _c(79);
  const {
    control,
    formState: t0
  } = useFormContext();
  const {
    errors
  } = t0;
  const {
    tenant
  } = useTenant();
  const t1 = tenant?.subDomain;
  let t2;
  if ($[0] !== t1) {
    t2 = isPatientMrnEnabledTenant(t1);
    $[0] = t1;
    $[1] = t2;
  } else {
    t2 = $[1];
  }
  const isExternalIdReadOnly = t2;
  let t3;
  if ($[2] === Symbol.for("react.memo_cache_sentinel")) {
    t3 = <Label htmlFor="firstName" mandatory={true}>First Name</Label>;
    $[2] = t3;
  } else {
    t3 = $[2];
  }
  let t4;
  if ($[3] !== errors.firstName) {
    t4 = t5 => {
      const {
        field
      } = t5;
      return <Input id="firstName" placeholder="Enter first name" {...field} required={true} className="w-full" hasError={!!errors.firstName} errorMessage={errors.firstName?.message?.toString() ?? "Unexpected error"} />;
    };
    $[3] = errors.firstName;
    $[4] = t4;
  } else {
    t4 = $[4];
  }
  let t5;
  if ($[5] !== control || $[6] !== t4) {
    t5 = <div>{t3}<Controller name="firstName" control={control} defaultValue="" render={t4} /></div>;
    $[5] = control;
    $[6] = t4;
    $[7] = t5;
  } else {
    t5 = $[7];
  }
  let t6;
  if ($[8] === Symbol.for("react.memo_cache_sentinel")) {
    t6 = <Label htmlFor="middleName">Middle Name</Label>;
    $[8] = t6;
  } else {
    t6 = $[8];
  }
  let t7;
  if ($[9] !== errors.middleName) {
    t7 = t8 => {
      const {
        field: field_0
      } = t8;
      return <Input id="middleName" placeholder="Enter middle name" {...field_0} value={field_0.value ?? ""} className="w-full" hasError={!!errors.middleName} errorMessage={errors.middleName?.message?.toString()} />;
    };
    $[9] = errors.middleName;
    $[10] = t7;
  } else {
    t7 = $[10];
  }
  let t8;
  if ($[11] !== control || $[12] !== t7) {
    t8 = <div>{t6}<Controller name="middleName" control={control} defaultValue="" render={t7} /></div>;
    $[11] = control;
    $[12] = t7;
    $[13] = t8;
  } else {
    t8 = $[13];
  }
  let t9;
  if ($[14] === Symbol.for("react.memo_cache_sentinel")) {
    t9 = <Label htmlFor="lastName" mandatory={true}>Last Name</Label>;
    $[14] = t9;
  } else {
    t9 = $[14];
  }
  let t10;
  if ($[15] !== errors.lastName) {
    t10 = t11 => {
      const {
        field: field_1
      } = t11;
      return <Input id="lastName" placeholder="Enter last name" {...field_1} required={true} className="w-full" hasError={!!errors.lastName} errorMessage={errors.lastName?.message?.toString() ?? "Unexpected error"} />;
    };
    $[15] = errors.lastName;
    $[16] = t10;
  } else {
    t10 = $[16];
  }
  let t11;
  if ($[17] !== control || $[18] !== t10) {
    t11 = <div>{t9}<Controller name="lastName" control={control} defaultValue="" render={t10} /></div>;
    $[17] = control;
    $[18] = t10;
    $[19] = t11;
  } else {
    t11 = $[19];
  }
  let t12;
  if ($[20] === Symbol.for("react.memo_cache_sentinel")) {
    t12 = <Label htmlFor="dateOfBirth">Date of Birth</Label>;
    $[20] = t12;
  } else {
    t12 = $[20];
  }
  let t13;
  if ($[21] !== control) {
    t13 = <div>{t12}<Controller name="dateOfBirth" control={control} render={_temp} /></div>;
    $[21] = control;
    $[22] = t13;
  } else {
    t13 = $[22];
  }
  let t14;
  if ($[23] === Symbol.for("react.memo_cache_sentinel")) {
    t14 = <Label htmlFor="phoneNumber" mandatory={true}>Phone Number</Label>;
    $[23] = t14;
  } else {
    t14 = $[23];
  }
  let t15;
  if ($[24] !== errors.phoneNumber) {
    t15 = t16 => {
      const {
        field: t17
      } = t16;
      const {
        onChange: onChange_0,
        value: value_0,
        ...field_2
      } = t17;
      return <PhoneNumberInput id="phoneNumber" placeholder="Enter phone number" {...field_2} value={value_0 || ""} onChange={e => onChange_0(e.target.value)} required={true} className="w-full" hasError={!!errors.phoneNumber} errorMessage={errors.phoneNumber?.message?.toString() ?? "Unexpected error"} />;
    };
    $[24] = errors.phoneNumber;
    $[25] = t15;
  } else {
    t15 = $[25];
  }
  let t16;
  if ($[26] !== control || $[27] !== t15) {
    t16 = <div>{t14}<Controller name="phoneNumber" control={control} defaultValue="" render={t15} /></div>;
    $[26] = control;
    $[27] = t15;
    $[28] = t16;
  } else {
    t16 = $[28];
  }
  let t17;
  if ($[29] === Symbol.for("react.memo_cache_sentinel")) {
    t17 = <Label htmlFor="email">Email</Label>;
    $[29] = t17;
  } else {
    t17 = $[29];
  }
  let t18;
  if ($[30] !== errors.email) {
    t18 = t19 => {
      const {
        field: field_3
      } = t19;
      return <Input id="email" type="email" placeholder="Enter email address" {...field_3} className="w-full" hasError={!!errors.email} errorMessage={errors.email?.message?.toString() ?? "Unexpected error"} />;
    };
    $[30] = errors.email;
    $[31] = t18;
  } else {
    t18 = $[31];
  }
  let t19;
  if ($[32] !== control || $[33] !== t18) {
    t19 = <div>{t17}<Controller name="email" control={control} defaultValue="" render={t18} /></div>;
    $[32] = control;
    $[33] = t18;
    $[34] = t19;
  } else {
    t19 = $[34];
  }
  let t20;
  if ($[35] === Symbol.for("react.memo_cache_sentinel")) {
    t20 = <Label htmlFor="externalId">External ID</Label>;
    $[35] = t20;
  } else {
    t20 = $[35];
  }
  let t21;
  if ($[36] !== errors.externalId || $[37] !== isExternalIdReadOnly) {
    t21 = t22 => {
      const {
        field: field_4
      } = t22;
      return <Input id="externalId" placeholder="Enter external ID" {...field_4} disabled={isExternalIdReadOnly} readOnly={isExternalIdReadOnly} className="w-full" hasError={!!errors.externalId} errorMessage={errors.externalId?.message as string} />;
    };
    $[36] = errors.externalId;
    $[37] = isExternalIdReadOnly;
    $[38] = t21;
  } else {
    t21 = $[38];
  }
  let t22;
  if ($[39] !== control || $[40] !== t21) {
    t22 = <div>{t20}<Controller name="externalId" control={control} defaultValue="" render={t21} /></div>;
    $[39] = control;
    $[40] = t21;
    $[41] = t22;
  } else {
    t22 = $[41];
  }
  let t23;
  if ($[42] === Symbol.for("react.memo_cache_sentinel")) {
    t23 = <Label htmlFor="mrn">MRN</Label>;
    $[42] = t23;
  } else {
    t23 = $[42];
  }
  let t24;
  if ($[43] !== control) {
    t24 = <Controller name="mrn" control={control} defaultValue="" render={_temp2} />;
    $[43] = control;
    $[44] = t24;
  } else {
    t24 = $[44];
  }
  let t25;
  if ($[45] === Symbol.for("react.memo_cache_sentinel")) {
    t25 = <p className="mt-1 text-caption text-muted-foreground">MRNs are assigned automatically and can't be changed.</p>;
    $[45] = t25;
  } else {
    t25 = $[45];
  }
  let t26;
  if ($[46] !== t24) {
    t26 = <div>{t23}{t24}{t25}</div>;
    $[46] = t24;
    $[47] = t26;
  } else {
    t26 = $[47];
  }
  let t27;
  if ($[48] === Symbol.for("react.memo_cache_sentinel")) {
    t27 = <Label htmlFor="gender">Gender</Label>;
    $[48] = t27;
  } else {
    t27 = $[48];
  }
  let t28;
  if ($[49] !== control) {
    t28 = <div>{t27}<Controller name="gender" control={control} render={_temp5} /></div>;
    $[49] = control;
    $[50] = t28;
  } else {
    t28 = $[50];
  }
  let t29;
  if ($[51] === Symbol.for("react.memo_cache_sentinel")) {
    t29 = <Label htmlFor="pronouns">Pronouns</Label>;
    $[51] = t29;
  } else {
    t29 = $[51];
  }
  let t30;
  if ($[52] !== errors.pronouns) {
    t30 = t31 => {
      const {
        field: field_7
      } = t31;
      return <Input id="pronouns" placeholder="e.g., he/him, she/her, they/them" {...field_7} value={field_7.value || ""} className="w-full" hasError={!!errors.pronouns} errorMessage={errors.pronouns?.message as string} />;
    };
    $[52] = errors.pronouns;
    $[53] = t30;
  } else {
    t30 = $[53];
  }
  let t31;
  if ($[54] !== control || $[55] !== t30) {
    t31 = <div>{t29}<Controller name="pronouns" control={control} render={t30} /></div>;
    $[54] = control;
    $[55] = t30;
    $[56] = t31;
  } else {
    t31 = $[56];
  }
  let t32;
  if ($[57] === Symbol.for("react.memo_cache_sentinel")) {
    t32 = <Label htmlFor="preferredName">Preferred Name</Label>;
    $[57] = t32;
  } else {
    t32 = $[57];
  }
  let t33;
  if ($[58] !== errors.preferredName) {
    t33 = t34 => {
      const {
        field: field_8
      } = t34;
      return <Input id="preferredName" placeholder="Nickname or preferred name" {...field_8} value={field_8.value || ""} className="w-full" characterLimit={255} hasError={!!errors.preferredName} errorMessage={errors.preferredName?.message as string} />;
    };
    $[58] = errors.preferredName;
    $[59] = t33;
  } else {
    t33 = $[59];
  }
  let t34;
  if ($[60] !== control || $[61] !== t33) {
    t34 = <div>{t32}<Controller name="preferredName" control={control} render={t33} /></div>;
    $[60] = control;
    $[61] = t33;
    $[62] = t34;
  } else {
    t34 = $[62];
  }
  const t35 = errors.leadSource as any;
  let t36;
  if ($[63] !== control || $[64] !== t35) {
    t36 = <div><LeadSourceSelect name="leadSource" control={control} error={t35} /></div>;
    $[63] = control;
    $[64] = t35;
    $[65] = t36;
  } else {
    t36 = $[65];
  }
  let t37;
  if ($[66] !== t11 || $[67] !== t13 || $[68] !== t16 || $[69] !== t19 || $[70] !== t22 || $[71] !== t26 || $[72] !== t28 || $[73] !== t31 || $[74] !== t34 || $[75] !== t36 || $[76] !== t5 || $[77] !== t8) {
    t37 = <div className="grid grid-cols-1 gap-4 md:grid-cols-2">{t5}{t8}{t11}{t13}{t16}{t19}{t22}{t26}{t28}{t31}{t34}{t36}</div>;
    $[66] = t11;
    $[67] = t13;
    $[68] = t16;
    $[69] = t19;
    $[70] = t22;
    $[71] = t26;
    $[72] = t28;
    $[73] = t31;
    $[74] = t34;
    $[75] = t36;
    $[76] = t5;
    $[77] = t8;
    $[78] = t37;
  } else {
    t37 = $[78];
  }
  return t37;
};
const MarketingPreferencesSection = () => {
  const $ = _c(18);
  const {
    control
  } = useFormContext();
  let t0;
  if ($[0] === Symbol.for("react.memo_cache_sentinel")) {
    t0 = <Label className="text-body-emphasis">Marketing Preferences</Label>;
    $[0] = t0;
  } else {
    t0 = $[0];
  }
  let t1;
  if ($[1] === Symbol.for("react.memo_cache_sentinel")) {
    t1 = <span className="text-body text-foreground-secondary">SMS Marketing</span>;
    $[1] = t1;
  } else {
    t1 = $[1];
  }
  let t2;
  if ($[2] !== control) {
    t2 = <div className="space-y-1">{t1}<Controller name="optedInToSmsMarketing" control={control} render={_temp7} /></div>;
    $[2] = control;
    $[3] = t2;
  } else {
    t2 = $[3];
  }
  let t3;
  if ($[4] === Symbol.for("react.memo_cache_sentinel")) {
    t3 = <span className="text-body text-foreground-secondary">Email Marketing</span>;
    $[4] = t3;
  } else {
    t3 = $[4];
  }
  let t4;
  if ($[5] !== control) {
    t4 = <div className="space-y-1">{t3}<Controller name="optedInToEmailMarketing" control={control} render={_temp9} /></div>;
    $[5] = control;
    $[6] = t4;
  } else {
    t4 = $[6];
  }
  let t5;
  if ($[7] === Symbol.for("react.memo_cache_sentinel")) {
    t5 = <span className="text-body text-foreground-secondary">Social Media Consent</span>;
    $[7] = t5;
  } else {
    t5 = $[7];
  }
  let t6;
  if ($[8] !== control) {
    t6 = <div className="space-y-1">{t5}<Controller name="optedInToSocialMedia" control={control} render={_temp11} /></div>;
    $[8] = control;
    $[9] = t6;
  } else {
    t6 = $[9];
  }
  let t7;
  if ($[10] === Symbol.for("react.memo_cache_sentinel")) {
    t7 = <span className="text-body text-foreground-secondary">AI Scribe Consent</span>;
    $[10] = t7;
  } else {
    t7 = $[10];
  }
  let t8;
  if ($[11] !== control) {
    t8 = <div className="space-y-1">{t7}<Controller name="aiScribeConsent" control={control} render={_temp13} /></div>;
    $[11] = control;
    $[12] = t8;
  } else {
    t8 = $[12];
  }
  let t9;
  if ($[13] !== t2 || $[14] !== t4 || $[15] !== t6 || $[16] !== t8) {
    t9 = <div className="space-y-4">{t0}<div className="space-y-4">{t2}{t4}{t6}{t8}</div></div>;
    $[13] = t2;
    $[14] = t4;
    $[15] = t6;
    $[16] = t8;
    $[17] = t9;
  } else {
    t9 = $[17];
  }
  return t9;
};
export default DemographicEditDialog;
function _temp(t0) {
  const {
    field: t1
  } = t0;
  const {
    onChange,
    value
  } = t1;
  return <DateOfBirthInput value={value} onChange={onChange} className="w-full" maxDate={new Date()} />;
}
function _temp2(t0) {
  const {
    field: field_5
  } = t0;
  return <Input id="mrn" placeholder="Not generated yet" {...field_5} disabled={true} readOnly={true} className="w-full" />;
}
function _temp3(value_1) {
  return formatToHumanReadable(value_1);
}
function _temp4(gender) {
  return <SearchSelectItem key={gender} value={gender}>{formatToHumanReadable(gender)}</SearchSelectItem>;
}
function _temp5(t0) {
  const {
    field: field_6
  } = t0;
  return <SearchSelect value={field_6.value || ""} onValueChange={field_6.onChange}><SearchSelectTrigger className="w-full"><SearchSelectValue placeholder="Select gender" formatValue={_temp3} /></SearchSelectTrigger><SearchSelectContent searchPlaceholder="Search gender..." emptyMessage="No gender found.">{Object.values(Gender).map(_temp4)}</SearchSelectContent></SearchSelect>;
}
function _temp6(option) {
  return <SelectItem key={option.value} value={option.value}>{option.label}</SelectItem>;
}
function _temp7(t0) {
  const {
    field
  } = t0;
  return <Select value={toSelectValue(field.value)} onValueChange={val => field.onChange(fromSelectValue(val))}><SelectTrigger><SelectValue /></SelectTrigger><SelectContent>{MARKETING_OPTIONS.map(_temp6)}</SelectContent></Select>;
}
function _temp8(option_0) {
  return <SelectItem key={option_0.value} value={option_0.value}>{option_0.label}</SelectItem>;
}
function _temp9(t0) {
  const {
    field: field_0
  } = t0;
  return <Select value={toSelectValue(field_0.value)} onValueChange={val_0 => field_0.onChange(fromSelectValue(val_0))}><SelectTrigger><SelectValue /></SelectTrigger><SelectContent>{MARKETING_OPTIONS.map(_temp8)}</SelectContent></Select>;
}
function _temp10(option_1) {
  return <SelectItem key={option_1.value} value={option_1.value}>{option_1.label}</SelectItem>;
}
function _temp11(t0) {
  const {
    field: field_1
  } = t0;
  return <Select value={toSelectValue(field_1.value)} onValueChange={val_1 => field_1.onChange(fromSelectValue(val_1))}><SelectTrigger><SelectValue /></SelectTrigger><SelectContent>{MARKETING_OPTIONS.map(_temp10)}</SelectContent></Select>;
}
function _temp12(option_2) {
  return <SelectItem key={option_2.value} value={option_2.value}>{option_2.label}</SelectItem>;
}
function _temp13(t0) {
  const {
    field: field_2
  } = t0;
  return <Select value={toSelectValue(field_2.value)} onValueChange={val_2 => field_2.onChange(fromSelectValue(val_2))}><SelectTrigger><SelectValue /></SelectTrigger><SelectContent>{MARKETING_OPTIONS.map(_temp12)}</SelectContent></Select>;
}